import { Icon } from '@/components/Icon'
import styles from './StarRating.module.css'
import cx from 'classnames'
import { RawButton } from '@/components/Button'
import { useHover } from 'react-aria'
import { useRef, useState } from 'react'

interface StarRatingProps {
  value?: number
  max?: number
  readOnly?: boolean
  isDisabled?: boolean
  onChange?: (value: number) => unknown
  testId?: string
}

/**
 * A component that displays an interactive star rating system with
 *  a configurable number of stars
 */
export function StarRating({
  value = 0,
  onChange,
  max = 5,
  isDisabled = false,
  readOnly = false,
  testId,
}: StarRatingProps) {
  const [valueBeingHoveredOn, setValueBeingHoveredOn] = useState(0)

  const createChangeHandler = (newValue: number) => () => {
    if (readOnly || isDisabled) {
      return
    }
    if (!onChange) {
      return
    }

    if (newValue !== value) {
      onChange(newValue)
    }
  }

  return (
    <div data-test={testId} className={styles.root}>
      {Array.from({ length: max }).map((_, index) => {
        const starValue = index + 1
        return (
          <Star
            aria-label={`Star ${starValue} of ${max}`}
            disabled={isDisabled}
            readOnly={readOnly}
            key={starValue}
            starValue={starValue}
            actualValue={value}
            valueBeingHoveredOn={valueBeingHoveredOn}
            onClick={createChangeHandler(starValue)}
            onHoverChange={(hovering) =>
              setValueBeingHoveredOn(
                readOnly || isDisabled ? 0 : hovering ? starValue : 0,
              )
            }
            testId={testId ? `${testId}-star-value-${starValue}` : undefined}
          />
        )
      })}
    </div>
  )
}

interface StarProps {
  starValue: number
  actualValue: number
  valueBeingHoveredOn: number
  disabled: boolean
  readOnly: boolean
  onClick(): unknown
  onHoverChange(hovering: boolean): unknown
  testId?: string
}

/**
 * A component representing a single star in the rating system that can be filled,
 * half-filled, or empty
 */
function Star({
  starValue,
  actualValue,
  valueBeingHoveredOn,
  onClick,
  onHoverChange,
  disabled,
  readOnly,
  testId,
}: StarProps) {
  const hoverRef = useRef(null)

  const valueToCompareWith =
    valueBeingHoveredOn === 0 ? actualValue : valueBeingHoveredOn
  const filled = valueToCompareWith >= starValue
  const half =
    starValue === Math.ceil(valueToCompareWith) &&
    Math.ceil(valueToCompareWith) > valueToCompareWith
  const { hoverProps } = useHover({
    onHoverChange: onHoverChange,
  })

  return (
    <RawButton
      type="button"
      className={cx(
        styles.star,
        disabled && styles.disabled,
        readOnly && styles.readOnly,
      )}
      onPress={onClick}
      ref={hoverRef}
      data-test={testId}
      {...hoverProps}
    >
      <span className={styles.under}>
        <Icon variant="secondary" name="rating-star" />
      </span>
      <span
        className={styles.over}
        style={{ width: half ? '50%' : filled ? '100%' : '0%' }}
      >
        <Icon name="rating-star" />
      </span>
    </RawButton>
  )
}
